/**
 * Training route paths as constants, so no client ever writes a URL as a
 * string. Same rule as `../iam/routes.ts` and `../catalog/routes.ts`: a typo in
 * a path is otherwise a 404 discovered at runtime, and here it does not
 * compile.
 */
export const TRAINING_ROUTES = {
    /**
     * — *"What was done to my account, and by whom"* (`D65`, `D69`).
     *
     * `/me/` and not `/accounts/:id/`, and the difference is the authorisation
     * rule made structural: the subject is read from the access token and there
     * is no place in the URL to name somebody else. A path parameter would put a
     * second account id on the wire and make "may I read this?" a check somebody
     * can forget to write — see `list-account-changes.ts`, which refuses the
     * mismatch anyway, because two defences against reading another person's
     * history is the right number.
     */
    changes: "/me/changes",
    /** `GET`/`PUT` — the three facts of the athlete profile (`D114`). */
    profile: "/me/profile",
    /** `GET`/`PUT` — goal, days, session length, experience (`D115`). */
    trainingConfig: "/me/training-config",
    /** `GET`/`PUT` — the whole checklist, replacing what is stored. */
    equipment: "/me/equipment",
    /**
     * `GET` the saved gyms, `POST` one (`D202`). A gym is a named preset of the
     * checklist above; the equipment in effect is still `equipment`.
     */
    gyms: "/me/gyms",
    /** `PUT` the whole preset, `DELETE` it (soft, idempotent). */
    gym: "/me/gyms/:id",
    /** `POST` — "Usar esta": the preset becomes the equipment in effect,
     *  written through the same use case as `PUT /me/equipment`. */
    gymUse: "/me/gyms/:id/use",
    /** `POST` — a batch of history from Strong or Hevy, parsed and matched on
     *  the device (`D201`). Idempotent per source session. */
    imports: "/me/imports",
    /** `GET` — every ended session's sets as Strong-compatible CSV, streamed
     *  (`D201`). */
    exportCsv: "/me/export.csv",
    /** `GET`/`PUT` — the whole body map, replacing what is stored. */
    injuries: "/me/injuries",
    /** `GET`/`PUT` — the seven readiness answers (`D98`). */
    parq: "/me/parq",
    /**
     * `POST` — one point appended to a body-measurement series (`D68`, `D69`);
     * `GET ?kind&from&to` — the series, one kind at a time (`D154`).
     *
     * The only `/me/*` write here that is not a whole-resource `PUT`, and the
     * verb says so: nothing replaces, the series IS the data. It exists because
     * `D126` took the model off the intake's data path, and the weight was the
     * one Session 1 answer whose only writer was an agent tool
     * (`measurement.ts` tells the whole story).
     */
    measurements: "/me/measurements",
    /**
     * `DELETE` — one point off the series, softly (`D53`, `D154`). Server
     * pattern. A correction is a new `POST` plus this on the wrong row, so the
     * series records that a correction happened.
     */
    measurement: "/me/measurements/:id",
    /**
     * `GET ?family=` — every series of one kind family, each reduced to its
     * newest point and the one before (`D207`). The "Medidas" card's one
     * read. NOT `/me/measurements/summary`: that would sit on the `:id`
     * pattern above and be one typo away from a `DELETE` of a row named
     * "summary".
     */
    measurementSummary: "/me/measurement-summary",
    /**
     * `GET` the record, `POST` a grant, `DELETE` to revoke (`D21`).
     *
     * Photographs of a body and the numbers derived from them are sensitive
     * personal data under the LGPD, and `bodycomp` accepts a `consent` field and
     * stores nothing with it — so the record is ours or it does not exist.
     * `POST` answers 201 for a new grant and 200 when the same version is
     * already active; `DELETE` is 204 and **deletes every photo while keeping
     * the numbers** (`D20`), which is why it is not a tombstone the app has to
     * reason about.
     */
    bodyPhotoConsent: "/me/body-photo-consent",
    /**
     * `GET` the history, `POST` two photos (`D16`, `D22`).
     *
     * The one `multipart/form-data` route in this table, and the only write here
     * whose body is not JSON: `meta` is the JSON text of
     * `bodyAssessmentCreateMeta`, `front` and `side` are JPEGs. 201 on a create,
     * 200 on a replay of the same client-minted `meta.id` (`D163`) — and the
     * replay matters more here than anywhere else, because a duplicate is two
     * uploads, two jobs on a queue that allows twenty a minute for everybody
     * (`D26`) and two rows in a trend the person reads as their body changing.
     */
    bodyAssessments: "/me/body-assessments",
    /**
     * `GET` one assessment — with a read-through poll while it is `pending`,
     * bounded on the ROW rather than by the client (`D26`) — and `DELETE` to
     * soft-delete it and remove its photos (204). Server pattern; a client
     * builds it with {@link bodyAssessmentPath}.
     */
    bodyAssessment: "/me/body-assessments/:id",
    /**
     * `GET` one photo as `image/jpeg`, `Cache-Control: private, no-store`.
     *
     * SIX segments, one more than the previous record holder
     * (`planItemAlternatives`) — see the note there: the nesting is what says a
     * photo belongs to an assessment, and the routes are not flattened to fit a
     * test. `:view` is one of `BODY_ASSESSMENT_PHOTO_VIEWS`, the same two words
     * as the multipart part names and the R2 key; anything else is a 404, as is
     * a photo deleted by a revocation (`D20`). Server pattern; a client builds
     * it with {@link bodyAssessmentPhotoPath}.
     */
    bodyAssessmentPhoto: "/me/body-assessments/:id/photos/:view",
    /**
     * `GET` the account's personal calibration, `DELETE` to drop it (204,
     * `D23`).
     *
     * There is no `PUT`, deliberately: an anchor is BORN inside a settle, from
     * the offset `bodycomp` computed out of a `reference` reading sent with a
     * create. A route that accepted an offset directly would accept a number
     * nobody measured.
     */
    bodyAnchor: "/me/body-anchor",
    /** `GET` — how far through Session 1 this person is (`D113`). */
    onboarding: "/me/onboarding",
    /**
     * `GET` the plan in effect (`?draft=1` for the draft), `POST` a new draft.
     *
     * A draft is `status: "archived"` — the plan is assembled while the student
     * keeps following the incumbent, and `publish` swaps them
     * (`training-plan.ts:36-50`). That is why `GET` needs a query parameter to
     * ask for the draft rather than a second path: they are the same resource in
     * two states.
     */
    plan: "/me/plan",
    /** `POST` — make a draft the active plan, archiving the previous one. */
    planPublish: "/me/plan/publish",
    /** `PUT` — the student moves the pen to a group or takes it back (`D51`).
     *  Student-only; the use case refuses any other actor. */
    planManagingGroup: "/me/plan/managing-group",
    /** `POST` — the engine proposes a plan, as a DRAFT (`D102`). */
    planGenerate: "/me/plan/generate",
    /**
     * `DELETE` — discard the account's draft (T32): a soft delete, 204, and
     * `GET /me/plan?draft=1` answers `null` after it. 404 `plan.no_draft` when
     * there is none. Accepting it whole is {@link TRAINING_ROUTES.planPublish}.
     */
    planDraft: "/me/plan/draft",
    /** `GET` — what the draft changes against the plan in effect
     *  (`draftDiffOutput`, T32). 404 `plan.no_draft` when there is no draft.
     *  Reads; never generates one (`D150`, `T-145`). */
    planDraftDiff: "/me/plan/draft/diff",
    /**
     * `POST` — reject ONE change of the draft (T32): its slot goes back to the
     * plan in effect's version, and the answer is the recomputed
     * `draftDiffOutput`. 404 `plan.change_not_found` for an unknown id. Server
     * pattern; a client builds it with `planDraftChangeRejectPath`.
     */
    planDraftChangeReject: "/me/plan/draft/changes/:changeId/reject",
    /**
     * `PUT` — accept the protocol the agent PROPOSED (`D102`), moving
     * `proposed_protocol_id` into `protocol_id`. A proposal that was never made
     * is refused: this route applies a standing proposal, it does not choose.
     */
    planProtocol: "/me/plan/protocol",
    /**
     * `POST` — week 1 of the plan in effect starts now (`D198`,
     * `planRestartWeeksInput` → `planRestartWeeksOutput`): `published_at`
     * moves, the lines, the protocol and the history do not.
     */
    planRestartWeeks: "/me/plan/restart-weeks",
    /** `POST` — a new session inside a plan (`D119`). */
    planSessions: "/me/plan/sessions",
    /**
     * `PUT` — the days of a plan in a new order (`D210`,
     * `planSessionsReorderInput`, 204). Every visible day, once each. Its own
     * path: `/me/plan/sessions/order` would match `planSession` with the id
     * "order".
     */
    planSessionOrder: "/me/plan/session-order",
    /**
     * `PUT` — group lines of one day that stand next to each other into a
     * superset, or ungroup them (`D246`, `planSupersetInput`, 204). The lines
     * name their day, so the path names none.
     */
    planSuperset: "/me/plan/superset",
    /**
     * `DELETE` — "not now" on the engine's standing suggestion (T12). No body,
     * 204, and 204 again on a second call: the app this replaced kept that tap
     * on the DEVICE, so the person's other phone asked the same question the
     * next morning.
     *
     * It withdraws the suggestion from `GET /me/plan` and nothing else. The row
     * stays, stamped, so the engine keeps seeing one standing and does not
     * write the same suggestion again at the end of the next workout; only
     * publishing another plan clears it (`D150`). There is no output field for
     * "dismissed" — `suggestion === null` is what a client reads.
     */
    planSuggestion: "/me/plan/suggestion",
    /** `PUT` (label, weekday, item order) / `DELETE`. Server pattern. */
    planSession: "/me/plan/sessions/:id",
    /**
     * `POST` — the engine rebuilds ONE workout of the plan in effect, in place
     * (`D102`, `D118`).
     *
     * A SEPARATE door from {@link TRAINING_ROUTES.planGenerate}, and the
     * separation is the whole point: `generate` drafts a NEW plan and `publish`
     * archives the incumbent, so routing *"rebuild Wednesday"* through the pair
     * would silently replace Monday and Friday too
     * (`publish-training-plan.ts`). This one writes that session's items and
     * touches nothing else — no draft, no publish, no archive, and the plan
     * keeps its id.
     *
     * It answers with the WHOLE `planView`, not the rebuilt day: the week
     * screen re-renders from one response rather than a rebuild followed by a
     * `GET`.
     */
    planSessionRegenerate: "/me/plan/sessions/:id/regenerate",
    /**
     * `GET ?muscleId&cursor&limit&locale` — the ranked feed for ADDING an
     * exercise that works `muscleId` to this session (`sessionCandidatesQuery`
     * → `slotCandidatesOutput`, `D182`). The slot feed's filters and ranking,
     * minus what the session already shows; like it, an empty pool is zero rows
     * and a reason, never a 404.
     */
    planSessionCandidates: "/me/plan/sessions/:id/candidates",
    /** `POST` — an exercise into a session. */
    planItems: "/me/plan/items",
    /** `DELETE` — an item off a session. Server pattern. */
    planItem: "/me/plan/items/:id",
    /**
     * `PUT` — another exercise into THIS item's slot, in one call
     * (`planItemReplaceInput` → `planItemView` of the new item). It replaces the
     * `DELETE` + `POST` + `PUT(order)` triple, which had no transaction across it
     * (`T-90`): a dropped connection left an empty slot, or the new exercise at
     * the end of the session. The triple's routes stay for other callers.
     */
    planItemReplace: "/me/plan/items/:id/replace",
    /**
     * `POST` — bring a soft-deleted item back, the undo of a remove or of a
     * replace (`planItemRestoreInput` → `planItemView` of the SAME row,
     * `D182`). Nothing is copied: the row keeps its id, slot and targets.
     */
    planItemRestore: "/me/plan/items/:id/restore",
    /**
     * `PUT` — change one line's sets, reps, hold, rest or load in place
     * (`planItemRetargetInput` → `planItemView` of the NEW line, `D197`). The
     * line is immutable, so the answer is a new row in the same slot, under
     * the client's `id` when it sent one; the undo is `planItemRestore` of the
     * old id with `replacing` the new one.
     */
    planItemTargets: "/me/plan/items/:id/targets",
    /** `PUT` — the line to another day of the same plan (`D214`,
     *  `planItemMoveInput` → `planItemView`, the NEW line). */
    planItemSession: "/me/plan/items/:id/session",
    /**
     * `GET` — at most three swaps for this item, each with a reason key
     * (`D106`).
     *
     * FIVE segments. `route-reachability.test.ts:152-154` assumed at most three
     * when it built its negative control; T26 raises that to eight. The routes
     * are not flattened to fit the test — the nesting is what says an
     * alternative belongs to an item.
     */
    planItemAlternatives: "/me/plan/items/:id/alternatives",
    /**
     * `GET ?cursor&limit&locale` — the editor's paged candidate feed for the
     * slot this item fills (`D04`, `T17`).
     *
     * A SECOND endpoint beside `planItemAlternatives`, never a widening of it.
     * That one answers *"offer me a swap"* and is capped at three, equal or
     * easier; this one answers *"deal me the next one"* — every candidate of
     * the slot's movement pattern the person could deliberately choose, harder
     * ones included, a page at a time. Both stay, because the deck's first run
     * and the session sheet still ask the first question.
     *
     * It never 404s on an empty pool: zero rows plus `exhaustedReasonKey` is
     * the answer, so the editor states the constraint the person could lift
     * instead of dead-ending (`select.ts`'s own stance on an unfillable slot).
     */
    planItemCandidates: "/me/plan/items/:id/candidates",
    /**
     * `GET` the refusal list, `POST` one exercise onto it (`D06`).
     *
     * Beside the engine's paths rather than beside `equipment` and `injuries`,
     * because it is not an intake answer: it is written from the deck, when
     * somebody refuses a card, and READ on every generate and every substitute
     * query. Bounded at `MAX_EXCLUSIONS`, which is why the `GET` has no cursor
     * — an unbounded `NOT IN` is a `T-6` row-read problem.
     *
     * `POST` is idempotent: 201 the first time, 200 for an exercise already on
     * the list, both with no body. At the cap it is 409 carrying
     * `exclusion.limit_reached` and `params: { max }`.
     */
    exclusions: "/me/exclusions",
    /**
     * `DELETE` — take one exercise back off the list. Keyed by the CATALOGUE
     * id and not by a surrogate, because `(account_id, exercise_id)` is the
     * row's whole identity; there is no id to carry.
     *
     * 204 whether or not there was anything to remove — `exclusions.ts` argues
     * it, and it is the same call `DEVICE_ROUTES.devices` records.
     */
    exclusion: "/me/exclusions/:exerciseId",
    /** `GET` the list, `POST` a plan saved as one. */
    templates: "/me/templates",
    /** `PUT` (rename) / `DELETE`. Server pattern. */
    template: "/me/templates/:id",
    /** `POST` — write a template's items into a plan. Server pattern. */
    templateApply: "/me/templates/:id/apply",
    /** `GET ?from&to` the history, `POST` to start a bout. */
    sessions: "/me/sessions",
    /** `GET` one session with its sets. Server pattern. */
    session: "/me/sessions/:id",
    /** `POST` — end it, and get the summary back. Server pattern. */
    sessionEnd: "/me/sessions/:id/end",
    /** `GET` — the summary of an ENDED session, recomputed from the stored
     *  rows by the same function `sessionEnd` answers with. 409
     *  `workout.session_not_ended` while it is open. Server pattern. */
    sessionSummary: "/me/sessions/:id/summary",
    /**
     * `POST` — log one set. The most-written route in the system.
     *
     * `GET ?locale` — that session's VISIBLE sets, oldest first, each with its
     * exercise's localized name (`sessionSetsOutput`, `D11`): the list a person
     * corrects a set from AFTER the session ended, when the workout store no
     * longer holds it. Server pattern; a client builds it with
     * {@link sessionSetsPath}.
     */
    sessionSets: "/me/sessions/:id/sets",
    /** `PUT` — the four questions after a session. Server pattern. */
    sessionFeedback: "/me/sessions/:id/feedback",
    /** `PUT` — the answer to the next-morning check-in (`D100`). */
    sessionSoreness: "/me/sessions/:id/soreness",
    /** `PUT` a correction (a new row superseding this one), `DELETE` to remove
     *  it. Server pattern. */
    set: "/me/sets/:id",
    /** `GET ?exerciseIds&from&to` — the chart behind `D104`. */
    exerciseHistory: "/me/exercise-history",
    /**
     * `GET` — one exercise's all-time records, rep maxes and one point per
     * session (`D199`): the exercise sheet's "Recordes" and "Gráficos".
     * Server-computed, because the device would otherwise need every set of
     * the exercise ever logged. Server pattern; a client builds it with
     * {@link exerciseRecordsPath}.
     */
    exerciseRecords: "/me/exercise-records/:exerciseId",
    /**
     * The person's own data about one exercise (`D200`). `GET` every note;
     * `PUT`/`DELETE` one, by catalogue id — a `PUT` replaces, a `DELETE`
     * answers 204 whether or not there was a note, so both replay safely
     * from the outbox. Server patterns; a client builds the item paths with
     * {@link exerciseNotePath} and {@link exercisePreferencePath}.
     */
    exerciseNotes: "/me/exercise-notes",
    exerciseNote: "/me/exercise-notes/:exerciseId",
    /** `GET` every favourite and preference; `PUT` one (`D200`). */
    exercisePreferences: "/me/exercise-preferences",
    exercisePreference: "/me/exercise-preferences/:exerciseId",
    /**
     * `GET ?exerciseId&locale` the live goals with the best each is measured
     * against; `POST` one under a client id (`D200`). `DELETE` one by id —
     * soft, and 204 on a second call. {@link exerciseGoalPath}.
     */
    exerciseGoals: "/me/exercise-goals",
    exerciseGoal: "/me/exercise-goals/:id",
    /**
     * `GET ?locale` — the person's level on every logged lift that has a
     * published standard (`D208`); `GET` one lift's, logged or not.
     * {@link strengthLevelPath}.
     */
    strengthLevels: "/me/strength-levels",
    strengthLevel: "/me/strength-levels/:exerciseId",
    /**
     * `GET` — the streak in full weeks, this week's count, whether a freeze
     * is available, the total of ended sessions and the milestones reached
     * (`D152`, `stats.ts`). Server-computed: the app's history window cannot
     * see a streak longer than itself.
     */
    stats: "/me/stats",
    /**
     * `GET ?weeks&locale` — the Progress screen's aggregates (T33,
     * `progress.ts`): weekly volume and sessions, this week's sets per target
     * muscle against the plan, the month's totals, the strength lines.
     */
    progress: "/me/progress",
    /**
     * `GET ?week&locale` — the Retrospectiva's six cards for one ISO week
     * (T34, `retrospective.ts`). Absent `week` means the last complete one; a
     * week that is not over answers `ready: false`, never an error.
     */
    retrospective: "/me/retrospective",
    /**
     * `GET ?period=week|month&week|month&locale` — the progress report
     * (`D252`, `progress-report.ts`): sets per muscle against the plan, the
     * muscles' distribution against the four spans before, which exercises
     * progress, hold or regress, the records, the volume and the consistency.
     * A span that is not over answers `ready: false`, never an error.
     */
    report: "/me/report",
    /**
     * `GET ?locale` — muscle readiness (`D203`, `recovery.ts`): per training
     * muscle, how recovered it is from the last seven days of sets, capped by
     * a sore check-in. A model of the logged sets only — no sleep, no stress.
     */
    recovery: "/me/recovery",
    /**
     * `POST ?locale` — "Treino rápido" (`D249`, `quick-workout.ts`): one
     * session for today from the minutes, the gym and the readiness model,
     * composed by the engine and answered. Writes nothing; starting it is
     * `POST /me/sessions` with no plan session.
     */
    quickWorkout: "/me/quick-workout",
    /**
     * `GET` — the lifetime totals and the achievements (`D204`, `lifetime.ts`).
     * Apart from `/me/stats` on purpose: that read is set-free (T26) and runs
     * on every home open; this one reads every set and runs on Progress.
     */
    lifetime: "/me/lifetime",
    /**
     * `GET ?sessionId` — whether a workout's summary asks the rating question
     * or the satisfaction check (`D251`, `app-feedback.ts`). A read; only a tap
     * writes.
     */
    feedbackPrompt: "/me/feedback/prompt",
    /** `POST` — one tap on either, idempotent on the client's id (`D251`). */
    feedback: "/me/feedback",
    /**
     * `GET ?locale` — the Today screen's one insight card (T36, `insight.ts`):
     * deterministic server rules, `null` when none fires.
     */
    insight: "/me/insight",
    /**
     * `GET` — the person's own conversations with the trainer, most recent
     * first, with titles and the unread dot (plan D32, D33, `conversation.ts`).
     */
    conversations: "/me/conversations",
    /** `PUT {}` → 204 — the person opened this conversation (plan D33). */
    conversationSeen: "/me/conversations/:id/seen",
    /**
     * `DELETE` → `{ deleted }` — erase one of the person's own conversations:
     * its Durable Object first, then the index row (`D153`'s order).
     */
    conversation: "/me/conversations/:id",
    /**
     * `PUT { title }` → `{ title }` — the person names the conversation. Stored
     * in its Durable Object, never in D1 (`D78`).
     */
    conversationTitle: "/me/conversations/:id/title",
    /**
     * `GET` — the four pending signals in one read: check-in, unread trainer
     * reply, ready Retrospectiva, draft plan (T38, plan D39, `pending.ts`).
     */
    pending: "/me/pending",
    /**
     * `POST` — `bodycomp`'s webhook delivery (`D16`, `D19`, T98). **The one path
     * in this table that is NOT under `/me/`, and the only one no client ever
     * calls.**
     *
     * Every other path here is subject-scoped: the account is read from the
     * access token and there is no place in the URL to name somebody else. This
     * one has no subject at all. It is `bodycomp` posting the compact job view —
     * byte-identical to `GET /v1/jobs/{id}` — for a job this Worker created, and
     * the account it belongs to is found from the job id in the signed body,
     * never from the URL and never from a token. `/me/` would be a lie about what
     * the request is.
     *
     * It carries no `:id` for the same reason, and deliberately: a job id in the
     * path would be an unauthenticated caller naming a row, and the value that
     * settles anything has to come from inside the signed bytes.
     *
     * It is in THIS table rather than in one of its own so that
     * `route-reachability.test.ts` probes it like every other declared path — a
     * webhook nothing answers is the exact failure that file exists for, and it
     * would be invisible here because no screen ever notices. It is also the
     * value `BODYCOMP_CALLBACK_URL` is built from in `apps/api/wrangler.jsonc`,
     * in BOTH the root block and `env.production`: changing this string means
     * changing those two, and rule 14 of the audit compares those blocks by NAME
     * and would not catch the mismatch (T96).
     *
     * ⚠️ **The HMAC signature is the authentication.** `X-Signature`,
     * `X-Signature-Timestamp` and the raw bytes, verified in constant time before
     * anything reads the body (`apps/api/src/routes/bodycomp-webhook.ts`).
     * It must never be added to a CORS allowlist: it is server-to-server, and no
     * browser has any business preflighting it.
     */
    bodycompWebhook: "/webhooks/bodycomp",
};
/**
 * ── THE BUILDERS, AND WHY EVERY ONE OF THEM ENCODES ─────────────────────────
 * The `:id` paths above are the SERVER's patterns, for Hono. A client must
 * never fetch one verbatim and must never string-replace `:id` itself — that
 * would be a second encoder to keep in agreement with the server's decoder.
 * Same rule, same reason, same shape as `exerciseDetailPath` in
 * `../catalog/routes.ts`'s neighbour.
 *
 * The return type is `` `/${string}` `` and not `string`: the mobile client's
 * `Fetcher` takes an absolute path, so a builder that ever produced a relative
 * one is a compile error here rather than a request resolved against whatever
 * base URL happened to be current.
 */
export function planSessionPath(id) {
    return `/me/plan/sessions/${encodeURIComponent(id)}`;
}
/** `POST` — rebuild that workout of the plan in effect (`D102`, `D118`). */
export function planSessionRegeneratePath(id) {
    return `${planSessionPath(id)}/regenerate`;
}
/** `GET` — the ranked candidates for adding to that session (`D182`). */
export function planSessionCandidatesPath(id) {
    return `${planSessionPath(id)}/candidates`;
}
/** The change id is opaque (`add:…`, `rm:…`) and carries a colon, so it is
 *  encoded like every other segment. */
export function planDraftChangeRejectPath(changeId) {
    return `/me/plan/draft/changes/${encodeURIComponent(changeId)}/reject`;
}
export function planItemPath(id) {
    return `/me/plan/items/${encodeURIComponent(id)}`;
}
/** `PUT` — replace that item's exercise in place. */
export function planItemReplacePath(id) {
    return `${planItemPath(id)}/replace`;
}
/** `POST` — restore that soft-deleted item (`D182`). */
export function planItemRestorePath(id) {
    return `${planItemPath(id)}/restore`;
}
export function planItemTargetsPath(id) {
    return `${planItemPath(id)}/targets`;
}
export function planItemSessionPath(id) {
    return `${planItemPath(id)}/session`;
}
export function planItemAlternativesPath(id) {
    return `${planItemPath(id)}/alternatives`;
}
export function planItemCandidatesPath(id) {
    return `${planItemPath(id)}/candidates`;
}
export function templatePath(id) {
    return `/me/templates/${encodeURIComponent(id)}`;
}
export function templateApplyPath(id) {
    return `${templatePath(id)}/apply`;
}
export function sessionPath(id) {
    return `/me/sessions/${encodeURIComponent(id)}`;
}
export function sessionEndPath(id) {
    return `${sessionPath(id)}/end`;
}
export function sessionSummaryPath(id) {
    return `${sessionPath(id)}/summary`;
}
export function sessionSetsPath(id) {
    return `${sessionPath(id)}/sets`;
}
export function sessionFeedbackPath(id) {
    return `${sessionPath(id)}/feedback`;
}
export function sessionSorenessPath(id) {
    return `${sessionPath(id)}/soreness`;
}
export function setPath(id) {
    return `/me/sets/${encodeURIComponent(id)}`;
}
/** `GET` — one exercise's records and series (`D199`). */
export function exerciseRecordsPath(exerciseId) {
    return `/me/exercise-records/${encodeURIComponent(exerciseId)}`;
}
/** `PUT`/`DELETE` one exercise's pinned note (`D200`). */
export function exerciseNotePath(exerciseId) {
    return `/me/exercise-notes/${encodeURIComponent(exerciseId)}`;
}
/** `PUT` one exercise's favourite and preference (`D200`). */
export function exercisePreferencePath(exerciseId) {
    return `/me/exercise-preferences/${encodeURIComponent(exerciseId)}`;
}
/** `DELETE` one goal (`D200`). */
export function exerciseGoalPath(id) {
    return `/me/exercise-goals/${encodeURIComponent(id)}`;
}
/** `GET` one lift's strength level (`D208`). */
export function strengthLevelPath(exerciseId) {
    return `/me/strength-levels/${encodeURIComponent(exerciseId)}`;
}
export function gymPath(id) {
    return `/me/gyms/${encodeURIComponent(id)}`;
}
export function gymUsePath(id) {
    return `${gymPath(id)}/use`;
}
export function measurementPath(id) {
    return `/me/measurements/${encodeURIComponent(id)}`;
}
/** `GET` (read-through while pending) / `DELETE` one photo assessment. */
export function bodyAssessmentPath(id) {
    return `/me/body-assessments/${encodeURIComponent(id)}`;
}
/**
 * `GET` one photo of that assessment.
 *
 * `view` is typed as `BodyAssessmentPhotoView` and not `string`, which is the
 * one builder here that constrains its own segment: the two words are a closed
 * set the server switches on, so a third one is a 404 rather than a photo, and
 * a compile error is a cheaper way to learn that. It is still encoded, for the
 * reason the whole block above encodes — the server decodes with Hono's own
 * `c.req.param`, and a builder that skipped it would be a second encoder to
 * keep in agreement with that decoder.
 */
export function bodyAssessmentPhotoPath(id, view) {
    return `${bodyAssessmentPath(id)}/photos/${encodeURIComponent(view)}`;
}
/**
 * The segment is a CATALOGUE id, not an opaque one, and it is encoded for the
 * same reason every builder above encodes: the server decodes with Hono's own
 * `c.req.param`, so a client that string-replaced `:exerciseId` itself would
 * be a second encoder to keep in agreement with that decoder.
 */
export function exclusionPath(exerciseId) {
    return `/me/exclusions/${encodeURIComponent(exerciseId)}`;
}
/** `PUT /me/conversations/:id/seen` for one conversation (plan D33). */
export function conversationSeenPath(id) {
    return `/me/conversations/${encodeURIComponent(id)}/seen`;
}
/** `DELETE /me/conversations/:id` for one conversation. */
export function conversationPath(id) {
    return `/me/conversations/${encodeURIComponent(id)}`;
}
/** `PUT /me/conversations/:id/title` for one conversation. */
export function conversationTitlePath(id) {
    return `/me/conversations/${encodeURIComponent(id)}/title`;
}
//# sourceMappingURL=routes.js.map